import { useState, useCallback, useRef, useEffect } from "react";
import api from "../api";

const POLL_INTERVAL = 2000;
const POLL_TIMEOUT = 60000;

export default function usePollRefreshStatus() {
  const [isPolling, setIsPolling] = useState(false);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);
  const activeRef = useRef(false);
  const onCompleteRef = useRef(null);
  const alertIdRef = useRef(null);

  const stopPolling = useCallback(() => {
    activeRef.current = false;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsPolling(false);
  }, []);

  const fetchAndCheck = useCallback(async () => {
    if (!activeRef.current) return;
    try {
      const resp = await api.getApplicantAlert(alertIdRef.current);
      if (!activeRef.current) return;
      if (resp.data?.status === "pending_ops_review") {
        stopPolling();
        onCompleteRef.current?.(resp.data);
      }
    } catch (err) {
      if (!activeRef.current) return;
      stopPolling();
      setError(err.message || "Failed to check alert status");
    }
  }, [stopPolling]);

  const startPolling = useCallback(
    (alertId, onComplete) => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      alertIdRef.current = alertId;
      onCompleteRef.current = onComplete;
      activeRef.current = true;
      setIsPolling(true);
      setError(null);

      fetchAndCheck();
      intervalRef.current = setInterval(fetchAndCheck, POLL_INTERVAL);

      timeoutRef.current = setTimeout(() => {
        if (!activeRef.current) return;
        stopPolling();
        setError("Refresh timed out after 60 seconds. Please reload and try again.");
      }, POLL_TIMEOUT);
    },
    [fetchAndCheck, stopPolling],
  );

  useEffect(() => {
    return () => {
      activeRef.current = false;
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return { isPolling, error, startPolling };
}
